import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useSignUp, useAuth, useUser } from '@clerk/clerk-react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { track } from '@/lib/analytics';
import { getPostAuthDestination } from '@/lib/postAuthRedirect';
import { FiChevronLeft } from 'react-icons/fi';
import { Button, Card, Input, PhoneNumberInput, normalizeUsPhoneNumber } from '@/components';

function SignUpPage() {
  const { isLoaded, signUp, setActive } = useSignUp();
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const { user, isLoaded: userLoaded } = useUser();
  const navigate = useNavigate();

  // Track screen view
  useEffect(() => {
    track('Sign Up Screen');
  }, []);

  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [pendingVerification, setPendingVerification] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Redirect after sign-up (admin phone gets portal choice)
  useEffect(() => {
    if (!authLoaded || !isSignedIn) return;

    const phoneFromForm = normalizeUsPhoneNumber(phone);
    if (phoneFromForm) {
      navigate({ to: getPostAuthDestination(phoneFromForm) });
      return;
    }

    if (!userLoaded) return;
    const phoneFromClerk = user?.primaryPhoneNumber?.phoneNumber ?? '';
    navigate({ to: getPostAuthDestination(phoneFromClerk) });
  }, [authLoaded, isSignedIn, userLoaded, user, phone, navigate]);

  const codeInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus code input when transitioning to verification step
  useEffect(() => {
    if (pendingVerification) {
      const timer = setTimeout(() => {
        codeInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [pendingVerification]);

  const onVerifyPress = useCallback(async () => {
    if (!isLoaded || !signUp) return;
    if (!code.trim()) {
      setError('Please enter the SMS code we sent you.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const attempt = await signUp.attemptPhoneNumberVerification({
        code: code.trim(),
      });

      if (attempt.status === 'complete') {
        // Just set the session active - the useEffect watching isSignedIn will handle navigation
        // This prevents a race condition where we navigate before Clerk updates isSignedIn
        await setActive({ session: attempt.createdSessionId });
      } else {
        console.error('Phone verification not complete:', attempt);
        const status = attempt.status ?? 'unknown';
        setError(`Verification incomplete. Status: ${status}`);
      }
    } catch (err: unknown) {
      console.error('Verification error:', err);
      const clerkError = err as { errors?: { message?: string }[]; message?: string };
      const message =
        clerkError?.errors?.[0]?.message ??
        clerkError?.message ??
        "That code didn't work. Please try again.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [code, isLoaded, signUp, setActive]);

  // Auto-submit when code reaches 6 digits
  useEffect(() => {
    if (pendingVerification && code.length === 6 && !isSubmitting && isLoaded) {
      onVerifyPress();
    }
  }, [code, pendingVerification, isSubmitting, isLoaded, onVerifyPress]);

  const onSignUpPress = useCallback(async () => {
    if (!isLoaded || !signUp) {
      console.warn('[SignUp] Clerk not loaded yet');
      return;
    }

    const phoneNumber = normalizeUsPhoneNumber(phone);
    if (!phoneNumber) {
      setError('Please enter a valid phone number.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      console.log('[SignUp] Creating sign-up with phone:', phoneNumber);
      await signUp.create({ phoneNumber });

      console.log('[SignUp] Preparing phone verification...');
      await signUp.preparePhoneNumberVerification({ strategy: 'phone_code' });

      setPendingVerification(true);
    } catch (err: unknown) {
      console.error('[SignUp] Error:', err);
      const clerkError = err as { errors?: { message?: string; longMessage?: string }[]; message?: string };
      const clerkErrors = clerkError?.errors;
      const longMessage = clerkErrors?.[0]?.longMessage;

      const message =
        longMessage ??
        clerkErrors?.[0]?.message ??
        clerkError?.message ??
        "Couldn't create your account. Please try again.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [isLoaded, phone, signUp]);

  return (
    <div className='min-h-screen bg-sky-light'>
      <div className='max-w-md mx-auto px-6 py-8'>
        {/* Back button */}
        <Link
          to='/auth'
          className='inline-flex items-center text-gray-900 hover:text-primary mb-6 -ml-2'
        >
          <FiChevronLeft size={24} />
        </Link>

        {/* Title */}
        <div className='text-center mb-8'>
          <h1
            className='text-3xl font-semibold text-gray-900 mb-3'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            {pendingVerification ? 'Verify your phone' : "Let's get started"}
          </h1>
          <p
            className='text-lg text-gray-500'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            {pendingVerification
              ? 'Enter the 6-digit code we sent you.'
              : "Enter your phone number and we'll text you a one-time code"}
          </p>
        </div>

        {/* Card */}
        <Card>
          {!pendingVerification ? (
            <>
              <PhoneNumberInput
                label='Phone number'
                value={phone}
                onChangeText={setPhone}
                disabled={isSubmitting}
                autoFocus
              />

              {error && (
                <p className='mt-3 text-sm text-red-500 text-center'>{error}</p>
              )}

              <div className='mt-4'>
                <Button
                  fullWidth
                  disabled={!isLoaded || isSubmitting}
                  loading={isSubmitting}
                  onClick={onSignUpPress}
                >
                  Send code
                </Button>
              </div>
            </>
          ) : (
            <>
              <Input
                ref={codeInputRef}
                label='Code'
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder='123456'
                type='text'
                inputMode='numeric'
                maxLength={6}
                disabled={isSubmitting}
                autoFocus
              />

              {error && (
                <p className='mt-3 text-sm text-red-500 text-center'>{error}</p>
              )}

              <div className='mt-4'>
                <Button
                  fullWidth
                  disabled={!isLoaded || isSubmitting}
                  loading={isSubmitting}
                  onClick={onVerifyPress}
                >
                  Continue
                </Button>
              </div>

              <div className='mt-3'>
                <Button
                  variant='secondary'
                  fullWidth
                  disabled={isSubmitting}
                  onClick={() => {
                    setPendingVerification(false);
                    setCode('');
                    setError('');
                  }}
                >
                  Use a different number
                </Button>
              </div>
            </>
          )}
        </Card>

        <div className='text-center mt-6 mb-3'>
          <Link
            to='/auth/pro/sign-up'
            className='text-sm text-primary font-semibold hover:underline'
          >
            Lawyer, mediator, or coach? Sign up here
          </Link>
        </div>

        {/* Footer link */}
        <div className='flex items-center justify-center gap-2 mt-2'>
          <span className='text-gray-500'>Already have an account?</span>
          <Link to='/auth/sign-in' className='text-gray-900 font-bold hover:underline'>
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/auth/sign-up')({
  component: SignUpPage,
});
